/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FlexColumn, FlexRow } from '../GridsAndLayouts';
import Typography from '../Typography';
import './FeedProgressBar.scss';

const clampPercent = (n) => Math.min(100, Math.max(0, n));

const FeedProgressBar = ({
  label = 'Progress',
  value = 0,
  variant = 'none',
  fill,
  trackHeight = 2,
  showLabel = true,
  className = '',
  id,
  'aria-valuetext': ariaValueText,
  style,
  ...rest
}) => {
  const pct = clampPercent(typeof value === 'number' ? value : Number(value));
  const rootClass = classNames(
    'ds-feed-progress-bar',
    !fill && `ds-feed-progress-bar--variant-${variant}`,
    !showLabel && 'ds-feed-progress-bar--bar-only',
    className
  );
  const fillStyle = fill ? { width: `${pct}%`, background: fill } : { width: `${pct}%` };
  let resolvedTrackHeight;
  if (trackHeight != null) {
    resolvedTrackHeight = typeof trackHeight === 'number' ? `${trackHeight}px` : trackHeight;
  }
  const rootStyle = {
    ...(resolvedTrackHeight != null && { '--ds-feed-progress-bar-track-height': resolvedTrackHeight }),
    ...style,
  };

  return (
    <FlexColumn
      className={rootClass}
      style={Object.keys(rootStyle).length > 0 ? rootStyle : undefined}
      role="progressbar"
      align="start"
      gap={showLabel ? 9 : 0}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-valuetext={ariaValueText ?? (showLabel && label ? `${label}, ${pct}%` : `${pct}%`)}
      aria-labelledby={showLabel && id ? `${id}-label` : undefined}
      {...rest}
    >
      {showLabel ? (
        <FlexRow className="ds-feed-progress-bar__row" justify="between" align="center" fullWidth>
          <Typography
            component="div"
            variant=""
            className="ds-feed-progress-bar__label ds-t20"
            id={id ? `${id}-label` : undefined}
          >
            {label}
          </Typography>
          <Typography component="div" className="ds-feed-progress-bar__value ds-t18">
            {Math.round(pct)}%
          </Typography>
        </FlexRow>
      ) : null}
      <FlexRow className="ds-feed-progress-bar__track" align="stretch" justify="start" fullWidth>
        <FlexRow
          className="ds-feed-progress-bar__fill"
          style={{ display: 'block', ...fillStyle }}
          inline
          fullWidth={false}
          aria-hidden
        />
      </FlexRow>
    </FlexColumn>
  );
};

FeedProgressBar.propTypes = {
  label: PropTypes.node,
  value: PropTypes.number,
  variant: PropTypes.oneOf(['none', 'positive', 'negative', 'notice', 'neutral']),
  fill: PropTypes.string,
  /** Bar track height — number (px) or CSS length (e.g. `4`, `'6px'`). Also set via `--ds-feed-progress-bar-track-height` in CSS. */
  trackHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** When false, renders only the track (no label row or built-in %). Default true. */
  showLabel: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  'aria-valuetext': PropTypes.string,
  style: PropTypes.shape({}),
};

export { FeedProgressBar };
export default FeedProgressBar;
